import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { StudyNavigationService } from './study-navigation.service';
import { LoadConfigService } from './load-config.service';
import { UtilsService } from './utils.service';
import { InstructionService } from './instruction.service';
import { AppState } from 'src/state/app.state';

import { selectAppMeta } from 'src/state/app-meta/app-meta.selectors';

@Injectable({
  providedIn: 'root'
})
export class InstructionModificationService {
  private RID = '';
  private agencyRedirectUrl = '';

  constructor(
    private navigationService: StudyNavigationService,
    private utils: UtilsService,
    private loadConfigService: LoadConfigService,
    private instructionService: InstructionService,
    private store: Store<AppState>
  ) {
    this.getAgencyRedirectUrl();
  }

  getAgencyRedirectUrl() {
    //TODO: Fetching and building the agency redirect url needs to be
    //      done in a better way and shoudln't be hidden inside this class
    const secret = '22333';

    this.store.select(selectAppMeta).subscribe(state => {
      if (state.RID) this.RID = state.RID;
    });

    this.agencyRedirectUrl = `https://surveys.sample-cube.com/ending?RS=1&RID=${this.RID}&secret=${secret}`;
  }

  replacePlaceholders(instruction: string, instructionTitle: string, instructionSection: string): string {
    const blockMapping = this.instructionService.getTranslationsForTerm('BLOCK_MAPPING');

    // this is necessary to copy the instruction string
    let modifiedInstruction = (' ' + instruction)
      .slice(1)
      .replace(
        /\[STUDY_TIME\]/gi,
        `${
          this.navigationService.getAmountOfStudies() * 5 + this.getQuestionnaireTime()
        } ${this.instructionService.getTranslationsForTerm('MINUTES')}`
      )
      // TODO: insert correct block replacement (what is defined as a block, probably questionnaires and tests, but not media viewers)
      .replace(
        /\[BLOCK\]/gi,
        blockMapping[this.navigationService.getWasLastBlock() ? 'last' : this.navigationService.getTimelineIdx() - 1]
      )
      .replace(
        /\[OUTRO_FINISH_STUDY-BEFORE_LINK\]/gi,
        this.instructionService.getTranslationsForTerm('OUTRO_FINISH_STUDY')['BEFORE_LINK']
      )
      .replace(/\[AGENCY_REDIRECT_URL\]/gi, this.agencyRedirectUrl)
      .replace(
        /\[OUTRO_FINISH_STUDY-IN_LINK\]/gi,
        this.instructionService.getTranslationsForTerm('OUTRO_FINISH_STUDY')['IN_LINK']
      )
      .replace(
        /\[OUTRO_FINISH_STUDY-AFTER_LINK\]/gi,
        this.instructionService.getTranslationsForTerm('OUTRO_FINISH_STUDY')['AFTER_LINK']
      )
      .replace(
        /\[INTERMISSION_CONTINUE_BUTTON\]/gi,
        this.instructionService.getTranslationsForTerm('INTERMISSION_CONTINUE_BUTTON')
      );

    if (instructionTitle === 'welcome') {
      modifiedInstruction = this.addListToWelcomePage(modifiedInstruction, instructionTitle);
    }

    modifiedInstruction = this.replaceKeyPlaceholders(modifiedInstruction, instructionSection);

    modifiedInstruction = this.replaceIATPlaceholders(modifiedInstruction, instructionSection);

    return modifiedInstruction;
  }

  private addListToWelcomePage(instruction: string, instructionTitle): string {
    if (instructionTitle !== 'welcome') {
      return instruction;
    }

    /*
      Use general description of task like 'Entscheidungsaufgaben' and 'decision tasks'
    */
    const hasQuestionnaire = this.getQuestionnaireExistence();

    const introToParts = hasQuestionnaire
      ? this.instructionService.getTranslationsForTerm('WELCOME_PAGE_HAS_QUESTIONNAIRE')
      : this.instructionService.getTranslationsForTerm('WELCOME_PAGE_NO_QUESTIONNAIRE');

    const descriptionIAT = `<li>${this.instructionService.getTranslationsForTerm('WELCOME_PAGE_IAT_DESCRIPTION')}</li>`;

    const descriptionQuestionnaire = `<li>${this.instructionService.getTranslationsForTerm(
      'WELCOME_PAGE_QUESTIONNAIRE_DESCRIPTION'
    )}</li>`;

    const listOfParts = `${introToParts}<p><ul>${descriptionIAT}${
      hasQuestionnaire ? descriptionQuestionnaire : ''
    }</ul></p>`;

    return instruction + listOfParts;
  }

  private getQuestionnaireExistence() {
    const clientConfig = this.loadConfigService.getClientConfig();
    const questions = clientConfig['questions'];
    return questions[this.instructionService.getLanguageIdentifier()]?.length;
  }

  private getQuestionnaireTime() {
    const hasQuestionnaire = this.getQuestionnaireExistence();

    return hasQuestionnaire ? 5 : 0;
  }

  private replaceIATPlaceholders(instruction: string, instructionSection: string) {
    const relevantTests = ['IAT'];
    if (!instructionSection) return instruction;

    if (!relevantTests.includes(instructionSection)) return instruction;

    const keys = this.getKeys(instructionSection);
    let modifiedInstruction = (' ' + instruction).slice(1);

    const lng = this.instructionService.getLanguageIdentifier();

    if (keys) {
      modifiedInstruction = modifiedInstruction
        .replace(/\[IAT_KEY_LEFT\]/g, keys[0].toUpperCase())
        .replace(/\[IAT_KEY_RIGHT\]/g, keys[1].toUpperCase())
        .replace(/\[IAT_CONTINUE_BUTTON\]/g, 'Leerzeichen')
        .replace(/\[IAT_OR_IMAGES\]/g, this.getReplacementForPlaceholder('IAT_OR_IMAGES', lng))
        .replace(
          /\[IAT_CATEGORIES_DIFFERENTIATE\]/g,
          this.getReplacementForPlaceholder('IAT_CATEGORIES_DIFFERENTIATE', lng)
        )
        .replace(/\[IAT_LEFT_CATEGORIES\]/g, this.getReplacementForPlaceholder('IAT_LEFT_CATEGORIES', lng))
        .replace(/\[IAT_RIGHT_CATEGORIES\]/g, this.getReplacementForPlaceholder('IAT_RIGHT_CATEGORIES', lng))
        .replace(/\[IAT_PRACTICE_BLOCK\]/g, this.getReplacementForPlaceholder('IAT_PRACTICE_BLOCK', lng));
    }

    return modifiedInstruction;
  }

  private getCategoriesIncludeImages(lng = 'EN') {
    const currentTestConfig = this.navigationService.getCurrentTestConfig();
    const currentTestStimuli = currentTestConfig['stimuli'][lng];
    const categories = Object.keys(currentTestStimuli);

    const categoriesIncludesImage = {};

    for (const key of categories) {
      const fileTypeOfStimuli = currentTestStimuli[key].map(stimulus => this.utils.getMediaType(stimulus));
      const stimuliIncludeImage = fileTypeOfStimuli.includes('images');

      categoriesIncludesImage[key] = stimuliIncludeImage ? true : false;
    }

    return categoriesIncludesImage;
  }

  private getReplacementForPlaceholder(placeholder: string, lng = 'EN') {
    const replacements = this.instructionService.getTranslationsForLanguage(lng);
    const currentTestConfig = this.navigationService.getCurrentTestConfig();
    const stimuliPositions = currentTestConfig['stimuliPositions'][lng];
    const replacementTexts = replacements[placeholder];

    switch (placeholder) {
      case 'IAT_OR_IMAGES': {
        const categoriesIncludesImage = this.getCategoriesIncludeImages(lng);

        const hasCategoryWithImage = Object.values(categoriesIncludesImage).includes(true);

        if (hasCategoryWithImage) {
          return replacements[placeholder];
        }

        return '';
      }

      case 'IAT_CATEGORIES_DIFFERENTIATE': {
        const categoriesIncludesImage = this.getCategoriesIncludeImages(lng);

        const stimuliPositionsIncludesImage = this.utils.makeDeepCopy(stimuliPositions);

        for (const position in stimuliPositionsIncludesImage) {
          stimuliPositionsIncludesImage[position] = stimuliPositionsIncludesImage[position]
            .map(category => this.utils.capitalizeWord(category))
            .map(category => {
              const transformedCategory = categoriesIncludesImage[category]
                ? `${category}${replacementTexts['images']}`
                : category;

              return transformedCategory;
            });
        }

        const transformedPositions = {};
        for (const position in stimuliPositionsIncludesImage) {
          const categoriesForPosition = stimuliPositionsIncludesImage[position];
          if (categoriesForPosition.length > 1) {
            const concatCategoriesWithoutLast = categoriesForPosition
              .slice(0, categoriesForPosition.length - 1)
              .join(', ');
            const concatCategories = `${concatCategoriesWithoutLast} ${
              replacements['or']
            } ${categoriesForPosition[categoriesForPosition.length - 1]}`;

            transformedPositions[position] = concatCategories;
          } else {
            const concatCategories = `${categoriesForPosition[categoriesForPosition.length - 1]}`;

            transformedPositions[position] = concatCategories;
          }
        }

        let differentiateCategories = '';
        for (let i = 0; i < Object.keys(transformedPositions).length; i++) {
          differentiateCategories += transformedPositions[Object.keys(transformedPositions)[i]];
          if (i < Object.keys(transformedPositions).length - 1) {
            differentiateCategories += ' vs. ';
          }
        }

        return differentiateCategories;
      }

      case 'IAT_LEFT_CATEGORIES': {
        return this.concatCategories(stimuliPositions['left'], replacements);
      }
      case 'IAT_RIGHT_CATEGORIES': {
        return this.concatCategories(stimuliPositions['right'], replacements);
      }

      case 'IAT_PRACTICE_BLOCK': {
        const isPracticeBlock = currentTestConfig['isPracticeBlock'];
        if (isPracticeBlock) {
          return replacementTexts;
        }
        return '';
      }

      default:
        return placeholder;
    }
  }

  private concatCategories(categories: string[], replacementTexts) {
    const categoriesAsTitleCase = categories.map(category => this.utils.capitalizeWord(category));
    const categoriesConcat = categoriesAsTitleCase.join(` ${replacementTexts['or']} `);
    return categoriesConcat;
  }

  private replaceKeyPlaceholders(instruction: string, instructionSection: string): string {
    if (!instructionSection) return instruction;

    const keys = this.getKeys(instructionSection);

    let modifiedInstruction = (' ' + instruction).slice(1);

    if (keys) {
      if (keys.length == 2) {
        modifiedInstruction = modifiedInstruction
          .replace(/\[UNIMPORTANT\]/gi, keys[0].toUpperCase())
          .replace(/\[IMPORTANT\]/gi, keys[1].toUpperCase());
      } else if (keys.length === 4) {
        modifiedInstruction = modifiedInstruction
          .replace(/\[STRONGLY_DISAGREE\]/gi, keys[0].toUpperCase())
          .replace(/\[DISAGREE\]/gi, keys[1].toUpperCase())
          .replace(/\[AGREE\]/gi, keys[2].toUpperCase())
          .replace(/\[STRONGLY_AGREE\]/gi, keys[3].toUpperCase());
      } else {
        // TODO:#code check this weird code
        // eslint-disable-next-line no-self-assign
        modifiedInstruction = modifiedInstruction;
      }
    } else {
      // eslint-disable-next-line no-self-assign
      modifiedInstruction = modifiedInstruction;
    }

    return modifiedInstruction;
  }

  private getKeys(instructionSection: string): string[] {
    let keys;

    switch (instructionSection) {
      case 'persona':
      case 'OT':
      case 'modular':
      case 'brand': {
        keys = this.loadConfigService.getDecisionKeys();
        break;
      }

      case 'agreement': {
        keys = this.loadConfigService.getDecisionKeysAgreement();
        break;
      }

      case 'IAT':
      case 'conjoint':
      case 'priming': {
        keys = this.loadConfigService.getDecisionKeysIAT('IAT');
        break;
      }

      default:
        keys = [];
    }
    return keys;
  }
}
